<script setup lang="ts">
import { ref, watch } from "vue";
import { Bars3Icon, LockClosedIcon, MoonIcon, PowerIcon, SunIcon } from '@heroicons/vue/24/outline';
import { useRoute } from "vue-router";
import { TransitionRoot } from '@headlessui/vue'

import { pb } from "@/pocketbase";
import router from "@/router";
import LoadingView from "@/views/LoadingView.vue";
import tsgLogo from "@/assets/img/tsg-group-circle.png";

const route = useRoute();
const isLoggedIn = ref(pb.authStore.isValid && pb.authStore.model.verified);
const showMobileMenu = ref(false);

pb.authStore.onChange((token, model) => {
  isLoggedIn.value = !!token && model?.verified;
});

watch(
    () => route.fullPath,
    async () => {
      showMobileMenu.value = false;
    }
);

function logout() {
  pb.authStore.clear();
  router.push('/login');
}

function toggleTheme() {
  if (localStorage.theme === 'dark') {
    document.documentElement.classList.remove('dark')
    localStorage.theme = 'light';
  } else {
    document.documentElement.classList.add('dark')
    localStorage.theme = 'dark';
  }
  showMobileMenu.value = false;

  window.dispatchEvent(new CustomEvent('theme-changed', {
    detail: {
      theme: localStorage.theme,
    }
  }));
}

function toggleMenu() {
  showMobileMenu.value = !showMobileMenu.value;
}

function goToAdmin() {
  window.open(process.env.VUE_APP_POCKETBASE_URL + '/_/', '_blank');
}

const atTopOfPage = ref(true);
window.addEventListener('scroll', () => {
  if (window.scrollY > 0) {
    if (atTopOfPage.value) {
      atTopOfPage.value = false
    }
  } else if (!atTopOfPage.value) {
    atTopOfPage.value = true
  }
});
</script>

<template>
  <main class="lg:pb-12 w-full">
    <div :class="['sticky top-0 p-4 lg:p-0 z-10', atTopOfPage ? '' : 'shadow-lg']">
      <nav class="default">
        <div class="wrapper">
          <router-link :to="{ name: 'home' }" tag="div" class="flex items-center">
            <img :src="tsgLogo" class="max-w-[2rem] mr-2" alt="TSG Logo"/>
            <div class="text-sm font-bold tracking-tight text-gray-900 dark:text-white">
              <div class="font-bold">TSG R&amp;D</div>
              <div class="font-light">Collaboration Hub</div>
            </div>
          </router-link>

          <div class="hidden lg:block ml-12 menu" v-if="isLoggedIn">
            <router-link :to="{ name: 'home' }">Home</router-link>
            <router-link :to="{ name: 'business_units' }">Business Units</router-link>
            <router-link :to="{ name: 'tech_landscape' }">Tech Landscape</router-link>
            <router-link :to="{ name: 'events' }">Events</router-link>
            <router-link :to="{ name: 'resources' }">Resources</router-link>
          </div>

          <div class="hidden lg:block menu">
            <MoonIcon class="icon w-4 h-4 hidden dark:inline-block" @click="toggleTheme"/>
            <SunIcon class="icon w-4 h-4 inline-block dark:hidden" @click="toggleTheme"/>
            <template v-if="isLoggedIn">
              <LockClosedIcon class="icon w-4 h-4 inline-block" @click="goToAdmin"/>
              <PowerIcon class="icon w-4 h-4 inline-block" @click="logout"/>
            </template>
          </div>

          <div class="lg:hidden menu" v-if="isLoggedIn">
            <Bars3Icon class="icon w-8 h-8" @click="toggleMenu"/>
          </div>
        </div>
      </nav>
      <TransitionRoot
          :show="isLoggedIn && showMobileMenu"
          as="nav"
          class="mobile"
          enter="transform origin-top-left ease-in-out duration-500"
          enter-from="scale-y-0"
          enter-to="scale-y-100"
          leave="transform origin-top-left ease-in-out duration-500"
          leave-from="scale-y-100"
          leave-to="scale-y-0">
        <router-link :to="{ name: 'home' }">Home</router-link>
        <router-link :to="{ name: 'business_units' }">Business Units</router-link>
        <router-link :to="{ name: 'tech_landscape' }">Tech Landscape</router-link>
        <router-link :to="{ name: 'events' }">Events</router-link>
        <router-link :to="{ name: 'resources' }">Resources</router-link>
        <hr/>
        <a href="#" class="opacity-70" @click.prevent="toggleTheme">Toggle Theme</a>
        <a href="#" class="opacity-70" @click.prevent="logout">Sign Out</a>
      </TransitionRoot>
    </div>
    <suspense timeout="0">
      <template #default>
        <slot/>
      </template>
      <template #fallback>
        <LoadingView/>
      </template>
    </suspense>
  </main>
</template>

<style scoped lang="scss">
nav.default {
  @apply bg-gray-100 dark:bg-gray-700 p-4 -m-4 lg:m-0;

  .wrapper {
    @apply flex items-center justify-between flex-wrap;

    .menu {
      @apply space-x-4;

      a {
        @apply font-semibold text-sm;

        &.router-link-active {
          @apply border-b-4 border-primary-500;
        }
      }

      .icon {
        @apply cursor-pointer text-gray-900 dark:text-white;
      }
    }
  }
}

nav.mobile {
  @apply absolute bg-white dark:bg-gray-700 p-4 -m-4 mt-5 space-y-4 w-screen;

  a {
    @apply block font-semibold;

    &.router-link-active {
      @apply border-l-4 pl-2 border-primary-500;
    }
  }

  hr {
    @apply border-gray-200 dark:border-gray-600;
  }
}
</style>
